<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  userInvitationsApi,
  type UserInvitation,
} from '@/services/api/userInvitations';
import { useAppConfirm } from '@/composables/confirm';
import { useNotifier } from '@/composables/notifier';
import AccountSubNav from '@/views/account/AccountSubNav.vue';

const invitations = ref<UserInvitation[]>([]);
const newEmail = ref('');
const submitting = ref(false);
const confirm = useAppConfirm();
const notify = useNotifier();

async function reload(): Promise<void> {
  invitations.value = await userInvitationsApi.index();
}

async function create(): Promise<void> {
  if (newEmail.value.length === 0 || submitting.value) return;
  submitting.value = true;
  try {
    const created = await userInvitationsApi.create(newEmail.value);
    invitations.value = [...invitations.value, created];
    newEmail.value = '';
    notify.info('Invitation sent.');
  } catch {
    notify.warn('Invitation exists already');
  } finally {
    submitting.value = false;
  }
}

async function remove(invitation: UserInvitation): Promise<void> {
  const ok = await confirm({
    header: 'Remove Invitation',
    message: 'Are you sure you want to remove this invitation?',
  });
  if (!ok) return;
  await userInvitationsApi.remove(invitation.id);
  invitations.value = invitations.value.filter((i) => i.id !== invitation.id);
}

async function resend(invitation: UserInvitation): Promise<void> {
  await userInvitationsApi.resend(invitation.id);
  notify.info('Invitation resent.');
}

onMounted(reload);
</script>

<template>
  <div>
    <AccountSubNav />
    <section class="invitations">
      <h1>User Invitations</h1>

      <form class="invitations__new" @submit.prevent="create">
        <input
          v-model="newEmail"
          type="email"
          name="email"
          placeholder="invitee@example.com"
          required
        />
        <button type="submit" :disabled="submitting">Invite</button>
      </form>

      <table v-if="invitations.length" class="invitations__table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Sent</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="inv in invitations" :key="inv.id">
            <td>{{ inv.email }}</td>
            <td>{{ (inv as any).sent_at ?? '—' }}</td>
            <td>{{ (inv as any).expires_at ?? '—' }}</td>
            <td>
              <button type="button" @click="resend(inv)">Resend</button>
              <button type="button" @click="remove(inv)">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>No pending invitations.</p>
    </section>
  </div>
</template>

<style scoped>
.invitations {
  max-width: 50rem;
  margin: 1.5rem auto;
  padding: 1.5rem;
}
.invitations__new {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}
.invitations__new input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}
.invitations__new button {
  padding: 0.5rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  cursor: pointer;
}
.invitations__table {
  width: 100%;
  border-collapse: collapse;
}
.invitations__table th,
.invitations__table td {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}
.invitations__table button {
  margin-right: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  cursor: pointer;
}
</style>
